/**
 * HELP PAGE (FAQ)
 * 
 * Future features:
 * - Searchable FAQ
 * - Expandable accordion sections
 * - Contact support option
 * - Video tutorials
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { HelpCircle, ChevronDown, Search, Mail, BookOpen } from 'lucide-react';
import { PageHeader } from '../components/common';

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

const faqs: FAQItem[] = [

  {
    category: 'Getting Started',
    question: 'How do I create an account?',
    answer: 'Click the "Sign Up / Login" button in the navigation bar. You can sign up with your email address.'
  },
  {
    category: 'Getting Started',
    question: 'Is Polish ALL the Things! free to use?',
    answer: 'That depends entirely on your storage needs. We offer a free tier with all features included, but if you have a large collection, you may need to upgrade to a paid plan for additional storage.'
  },
  {
    category: 'Collection',
    question: 'How do I add items to my collection?',
    answer: `To add items individually, navigate to "My Items" → "All Items" and click the "+" button to get started.

To add items from a spreadsheet or AirTable, navigate to "My Items" → "All Items" and click the "Import more items" link.

You can add polish, plates, tools, acrylics, embellishments, powders, and more!`
  },
  {
    category: 'Collection',
    question: 'Can I upload photos of my nail polish?',
    answer: 'Absolutely! When adding or editing an item, you can upload multiple photos. You can also set a default photo that will be displayed on your collection cards.'
  },
  {
    category: 'Collection',
    question: 'Sometimes my brand or collection name looks slightly different than what I typed — why?',
    answer: 'Occasionally an admin normalizes brand, polish, and collection names to keep the community catalog consistent (for example, "essie nail lacquer" becomes "Essie"). Your items themselves are never deleted or moved — only the name text is corrected so everyone can find the same polish under one canonical entry.'
  },
  {
    category: 'Wishlist',
    question: 'What do ISO, DISO, and Unicorn mean?',
    answer: `💎 ISO (In Search Of) = Items you\'re looking for that are available.
 
 🔥 DISO (Desperately In Search Of) = Items that exist but are hard to find.
 
 🦄 Unicorn = Items that are extremely rare or don\'t exist yet.`
  },
  {
    category: 'Marketplace',
    question: 'How do I list items for destash?',
    answer: 'When viewing any item in your collection, you can toggle "List for Destash" to make it available in the marketplace. Set your price, condition, and whether you\'re open to swaps.'
  },
  {
    category: 'Privacy',
    question: 'Who can see my collection?',
    answer: 'You control your privacy settings. By default, your profile is private. You can make your profile, wishlist, and destash listings public or keep them private in Settings.'
  },
  {
    category: 'Privacy',
    question: 'Can I delete my account?',
    answer: 'Yes, you can delete your account at any time from Settings. This will permanently remove all your data including your collection, messages, and profile information.'
  },
  {
    category: 'Data & Storage',
    question: 'Where, how, and by who is our user data stored?',
    answer: 'Your data is stored securely on Google Cloud infrastructure through Firebase, which is Google\'s app development platform. Data is hosted in US data centers, encrypted in transit and at rest, and is subject to Google\'s enterprise-grade security standards. Only you and the app\'s administrator have access to your account data. We never sell or share your personal data with third parties.'
  },
  {
    category: 'Data & Storage',
    question: 'Is there any option to store data locally or in an iCloud account?',
    answer: 'Currently the app is web-based and stores data in the cloud, so there\'s no local or iCloud storage option. The upside is that your collection is accessible from any device — phone, tablet, or computer — just by logging in. You can export a CSV of your items at any time, and a photo exporting tool is also in the works.'
  },
  {
    category: 'Data & Storage',
    question: 'How does storage work?',
    answer: `The storage limit applies to photos only. Your collection records (item names, brands, tags, notes, etc.) are text data and don't count toward your storage limit — you can add unlimited items on any plan.

Our app automatically compresses photo uploads to keep file sizes reasonable. A typical compressed photo is around 100–200 KB, so on the free plan (50 MB) you could store roughly 250–500 photos. If you don't upload photos, you'll never hit the limit regardless of collection size.

To help gauge your usage, there's a storage bar in Account Settings that shows how much you've used.`
  },
  {
    category: 'Billing & Refunds',
    question: 'Can I get a refund?',
    answer: 'Yes! If you\'re not happy with your subscription, you can get a full refund within the first 7 days, no questions asked. After that, you can cancel anytime and keep access until your billing period ends. See our Refund Policy (linked in the footer) for full details.'
  },
  {
    category: 'Billing & Refunds',
    question: 'How do I cancel my subscription?',
    answer: 'You can cancel anytime from Settings. Your subscription won\'t renew, but you\'ll keep access to paid features until the end of your current billing period.'
  },
  {
    category: 'Billing & Refunds',
    question: 'What happens to my stuff if I downgrade?',
    answer: 'You\'ll have a 30-day grace period to reduce your storage to fit your new plan. During this time, you can view everything but can\'t add new items. We recommend using the export feature in Settings to back up your data. After 30 days, if you\'re still over the limit, we\'ll begin removing your oldest items — but we\'ll send you reminders before that happens.'
  },
  {
    category: 'Photos & Copyright',
    question: 'Can I upload photos I found online?',
    answer: 'You should only upload photos you took yourself or have permission to use. Brand promo images are generally okay for cataloging, but please don\'t upload other swatchers\' photos without their permission. The nail community takes photo credit seriously, and so do we!'
  },
  {
    category: 'Photos & Copyright',
    question: 'Who owns the photos I upload?',
    answer: 'You do! You always retain full ownership and copyright of your photos. We just need a limited license to display them within the app according to your privacy settings. See our Copyright Policy (linked in the footer) for details.'
  },
  {
    category: 'Photos & Copyright',
    question: 'Someone is using my swatch photos without permission!',
    answer: 'Please contact us through the "Report User/Content" option on our Contact page with a link to the infringing content. We take copyright seriously and will handle it promptly.'
  },
  {
    category: 'General',
    question: 'Was AI used at all in the creation of this app?',
    answer: 'Yes; I used Claude Code as a development tool.  Our instructors taught us how to do everything in class before they taught us how to use AI tools, so I only used it to help speed up the coding process.  It was a valuable learning experience for me.  AI is not always correct, and if you have no idea what you are doing it can take you off track pretty fast.  I checked and tested everything it generated to make sure it worked as intended and was safe.  No AI was used for any architecture, design, writing, or other creative work.'
  },
];

export default function HelpPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const filteredFaqs = faqs.filter(faq => 
    faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const categories = [...new Set(faqs.map(f => f.category))];

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* HEADER */}
        <PageHeader
          icon={HelpCircle}
          title="Help Center"
          subtitle="Find answers to common questions"
          className="mb-6"
        />

        {/* Search */}
        <div className="glass-card p-6 mb-8">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-glass-muted" />
            <input
              type="text"
              placeholder="Search for answers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 rounded-xl border border-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent bg-slate-800/60 text-glass placeholder:text-glass-muted font-body"
            />
          </div>
        </div>

        {/* Data Guide Card */}
        <Link
        to="/help/data-guide"
        className="glass-card p-6 mb-8 block hover:bg-slate-800/70 transition-default"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center flex-shrink-0">
              <BookOpen className="icon-md text-purple-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-glass font-body">
                📚 Data Best Practices Guide
              </h2>
              <p className="text-glass-secondary text-sm font-body">
                Learn how to organize your collection for the best experience
              </p>
            </div>
          </div>
        </Link>

        {/* FAQ Sections */}
        {categories.map(category => {
          const categoryFaqs = filteredFaqs.filter(f => f.category === category);
          if (categoryFaqs.length === 0) return null;

          return (
            <div key={category} className="mb-8">
              <h2 className="text-lg font-bold text-on-bg mb-4 px-2 font-body">
                {category}
              </h2>
              <div className="glass-card overflow-hidden whitespace-pre-line">
                {categoryFaqs.map((faq, index) => {
                  const globalIndex = faqs.indexOf(faq);
                  const isExpanded = expandedIndex === globalIndex;

                  return (
                    <div key={index} className={index > 0 ? 'border-t border-slate-600' : ''}>
                      <button
                        onClick={() => setExpandedIndex(isExpanded ? null : globalIndex)}
                        className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-700/50 transition-default"
                      >
                        <span className="font-medium text-glass pr-4 font-body">
                          {faq.question}
                        </span>
                        <ChevronDown className={`w-5 h-5 text-glass-muted flex-shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      </button>
                      {isExpanded && (
                        <div className="px-5 pb-5 text-glass-secondary font-body">
                          {faq.answer}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Contact Support */}
        <div className="glass-card p-8 text-center">
          <Mail className="icon-2xl text-glass mx-auto mb-4" />
          <h2 className="text-xl font-bold text-glass mb-2 font-body">
            Still have questions?
          </h2>
          <p className="text-glass-secondary mb-4 font-body">
            Can't find what you're looking for? We're here to help!
          </p>
          <a 
            href="/contact"
            className="inline-flex items-center gap-2 px-6 py-3 btn-primary rounded-xl font-medium transition-default font-body"
          >
            <Mail className="icon-sm" />
            Contact Support
          </a>
        </div>
      </div>
    </div>
  );
}
